// Combat maneuvers as buttons (qa-runs#398, the buttons): grapple, trip, bull rush and the rest, each listed from the
// table's system data (`systemRules.maneuvers`), never from here, and each sending `maneuver.request` naming the two
// tokens and nothing else. The server reads every number and decides the outcome. A grapple's round actions (damage,
// pin, move, escape; Pathfinder's maintain) are listed only while the token has what they need (grappling, grappled).
// The try-back a failure offers comes to whoever acts for the target as a button on their notice (TableState).
import { maneuversNow, type ManeuverView } from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { targetTool } from '../../board/targetTool';
import { hotDragProps } from '../../board/hotDrag';
import { BTN } from '../ui';

/** Large enough for a finger (44 px), as the hot bar's boxes are. */
export const TOUCH_BTN = `${BTN} min-h-[44px] min-w-[44px] px-3 touch-manipulation`;

/** The maneuvers this token may try now, by the system's data and its conditions. */
export function useManeuversOf(tokenId: string | null | undefined): ManeuverView[] {
  const { systemRules, store } = useTableState();
  const o = tokenId ? (store.state.objects as Record<string, { values?: { conditions?: string[] } }>)[tokenId] : undefined;
  return o ? maneuversNow(systemRules.maneuvers, o.values?.conditions) : [];
}

/** Send a maneuver: at the target given, else the next token tapped (`targetTool`). The outcome is told as a notice. */
export function useManeuver(): (attacker: string, attackerName: string, m: ManeuverView, target?: string | null) => void {
  const { act, setNotice } = useTableState();
  const send = (attacker: string, target: string, m: ManeuverView, ally?: string) =>
    act({ kind: 'maneuver.request', maneuver: m.key, attacker, target, ...(ally ? { ally } : {}) } as never, (reply) => {
      if (!reply.ok) setNotice(reply.reason ?? `${m.label} could not be made.`);
      else if (reply.notice) setNotice(reply.notice);
    });
  // Aid Another names a friend as well: the next token tapped after the foe.
  const go = (attacker: string, target: string, m: ManeuverView) => {
    if (!m.aid) { send(attacker, target, m); return; }
    targetTool.start(`${m.label}: the friend`, (ally) => {
      if (ally === attacker || ally === target) { setNotice(`${m.label}: tap a friend, not ${ally === attacker ? 'yourself' : 'the foe'}.`); return; }
      send(attacker, target, m, ally);
    });
  };
  return (attacker, attackerName, m, target) => {
    if (target && target !== attacker) { go(attacker, target, m); return; }
    targetTool.start(m.label, (t) => {
      if (t === attacker) { setNotice(`${attackerName} cannot ${m.label.toLowerCase()} itself.`); return; }
      go(attacker, t, m);
    });
  };
}

const why = (m: ManeuverView, at: string | undefined) =>
  `${m.label}${at ? ` at ${at}` : ': then tap the target'}${m.provokes ? '. Provokes an attack of opportunity unless you have the Improved feat.' : '.'}`;

/** The stances that are an action of their own (`features.combat.stances`): a button each, and a notice of what came of it. */
export function StanceButtons({ o }: { o: { id: string; name?: string } }) {
  const { systemRules, act, setNotice } = useTableState();
  const list = systemRules.actionStances ?? [];
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="stances" aria-label={`Defensive actions of ${o.name ?? 'this token'}`}>
      {list.map((s) => (
        <button key={s.key} type="button" className={`${TOUCH_BTN} border-sky-700`} data-testid={`stance-${s.key}`} title={`Until this creature's next turn: +${s.ac} to AC.`}
          onClick={() => act({ kind: 'stance.take', token: o.id, stance: s.key } as never, (reply) => setNotice(reply.ok ? reply.notice ?? `${s.label} taken.` : reply.reason ?? `${s.label} could not be taken.`))}>{s.label}</button>
      ))}
    </div>
  );
}

/** The maneuver buttons under a token's attacks (Properties' Actions). Each can be dragged onto a hot bar box. */
export function ManeuverButtons({ o, aimed }: { o: { id: string; name?: string }; aimed?: { id: string; name?: string } | undefined }) {
  const list = useManeuversOf(o.id);
  const make = useManeuver();
  if (!list.length) return null;
  const round = list.filter((m) => m.needs.length);
  const plain = list.filter((m) => !m.needs.length);
  const button = (m: ManeuverView) => (
    <button key={m.key} type="button" className={`${TOUCH_BTN} border-sky-700`} data-testid={`maneuver-${m.key}`}
      {...hotDragProps({ token: o.id, ref: `maneuver:${m.key}`, label: m.label })} title={why(m, aimed?.name)}
      onClick={() => make(o.id, o.name ?? 'This token', m, aimed?.id)}>{m.label}</button>
  );
  return (
    <div className="space-y-1" data-testid="maneuvers" aria-label={`Combat maneuvers of ${o.name ?? 'this token'}`}>
      {round.length > 0 && (
        <>
          <p className="font-semibold text-sky-300">In the grapple</p>
          <div className="flex flex-wrap gap-1">{round.map(button)}</div>
        </>
      )}
      <p className="font-semibold text-sky-300">Maneuvers</p>
      <div className="flex flex-wrap gap-1">{plain.map(button)}</div>
    </div>
  );
}
